import { Check, Copy } from 'lucide-react';
import { memo, useState, type ComponentProps, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { copyText } from '@/lib/browser';
import { cn } from '@/lib/utils';

/** Copies a code block's text, with a short "Copied" confirmation. */
function CopyCode({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={copied ? 'Copied' : 'Copy code'}
      className="text-muted-foreground hover:text-foreground hover:bg-accent absolute top-1.5 right-1.5 rounded-md p-1.5 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
      onClick={() =>
        void copyText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
      }
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}

const textOf = (node: ReactNode): string =>
  typeof node === 'string' || typeof node === 'number'
    ? String(node)
    : Array.isArray(node)
      ? node.map(textOf).join('')
      : node && typeof node === 'object' && 'props' in node
        ? textOf((node.props as { children?: ReactNode }).children)
        : '';

const components: Components = {
  p: ({ node: _, ...props }) => <p className="my-2 first:mt-0 last:mb-0" {...props} />,
  h1: ({ node: _, ...props }) => (
    <h3 className="mt-4 mb-2 text-base font-semibold first:mt-0" {...props} />
  ),
  h2: ({ node: _, ...props }) => (
    <h3 className="mt-4 mb-2 text-base font-semibold first:mt-0" {...props} />
  ),
  h3: ({ node: _, ...props }) => <h4 className="mt-3 mb-1.5 font-semibold first:mt-0" {...props} />,
  h4: ({ node: _, ...props }) => <h5 className="mt-3 mb-1.5 font-semibold first:mt-0" {...props} />,
  h5: ({ node: _, ...props }) => <h6 className="mt-3 mb-1 font-semibold first:mt-0" {...props} />,
  h6: ({ node: _, ...props }) => <h6 className="mt-3 mb-1 font-semibold first:mt-0" {...props} />,
  ul: ({ node: _, ...props }) => <ul className="my-2 list-disc space-y-1 pl-5" {...props} />,
  ol: ({ node: _, ...props }) => <ol className="my-2 list-decimal space-y-1 pl-5" {...props} />,
  li: ({ node: _, ...props }) => <li className="pl-0.5 marker:text-muted-foreground" {...props} />,
  strong: ({ node: _, ...props }) => (
    <strong className="text-foreground font-semibold" {...props} />
  ),
  blockquote: ({ node: _, ...props }) => (
    <blockquote className="text-muted-foreground my-2 border-l-2 pl-3" {...props} />
  ),
  hr: () => <hr className="my-4" />,
  // Links open outside the console; the model chose the URL, so no referrer or opener.
  // An unsafe URL (e.g. javascript:) arrives empty: show its text, not a dead link.
  a: ({ node: _, href, children, ...props }) =>
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="text-foreground underline underline-offset-2 hover:opacity-80"
        {...props}
      >
        {children}
      </a>
    ) : (
      <span>{children}</span>
    ),
  // Images from a reply would load from anywhere the model points: show a link instead.
  img: ({ src, alt }) =>
    typeof src === 'string' && /^https?:\/\//i.test(src) ? (
      <a href={src} target="_blank" rel="noopener noreferrer nofollow" className="underline">
        {alt || 'Image'}
      </a>
    ) : (
      <span>{alt}</span>
    ),
  table: ({ node: _, ...props }) => (
    <div className="my-3 overflow-x-auto rounded-lg border">
      <table className="w-full border-collapse text-left text-[0.95em]" {...props} />
    </div>
  ),
  thead: ({ node: _, ...props }) => <thead className="bg-muted/60" {...props} />,
  th: ({ node: _, ...props }) => <th className="border-b px-3 py-1.5 font-medium" {...props} />,
  td: ({ node: _, ...props }) => (
    <td className="border-b px-3 py-1.5 align-top [tr:last-child_&]:border-b-0" {...props} />
  ),
  pre: ({ node: _, children }) => (
    <div className="group relative my-3">
      <pre className="bg-muted overflow-x-auto rounded-lg p-3 font-mono text-xs leading-5">
        {children}
      </pre>
      <CopyCode text={textOf(children).replace(/\n$/, '')} />
    </div>
  ),
  code: ({ node: _, className, ...props }: ComponentProps<'code'> & { node?: unknown }) =>
    // Fenced blocks carry a language class (or sit in <pre>); inline code gets a chip.
    className?.startsWith('language-') ? (
      <code className={className} {...props} />
    ) : (
      <code
        className="bg-muted rounded px-1 py-0.5 font-mono text-[0.85em] [pre_&]:bg-transparent [pre_&]:p-0 [pre_&]:text-[1em]"
        {...props}
      />
    ),
};

/**
 * Renders an assistant reply written in Markdown (GitHub flavour: lists, tables, code). Raw HTML
 * in the text is shown as text, never rendered.
 */
export const Markdown = memo(function Markdown({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  return (
    <div className={cn('text-sm leading-relaxed [overflow-wrap:anywhere]', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
});
